/* -----------------Constants--------------- */

import { events } from "@constants/tracking";
import useAnalytics from "@hooks/useAnalytics";
import type {
	BaseItemSchema,
	ItemResponse,
} from "@memorang/types/src/session/new-session";
import { useEffect, useMemo } from "react";
import SpeakingContainer from "../components/itemtypes/question/SpeakingContainer";
import WritingContainer from "../components/itemtypes/question/WritingContainer";
/* -----------------Components--------------- */
import BaseItemTypeContainer from "./BaseItemTypeContainer";

interface Props {
	item: BaseItemSchema;
	toggleShowReadMore?: () => void;
	sessionId?: number;
}

const ResponseRenderer = ({
	item,
	response,
	toggleShowReadMore,
}: {
	item: BaseItemSchema;
	response: ItemResponse;
	toggleShowReadMore?: () => void;
}) => {
	const { metadata } = item;
	const originalItemType = metadata?.type!;

	switch (response.type) {
		case "choice":
		case "form":
		case "text":
			return (
				<BaseItemTypeContainer
					item={item}
					response={response}
					toggleShowReadMore={toggleShowReadMore}
					originalItemType={originalItemType}
				/>
			);
		case "recording":
			return (
				<SpeakingContainer
					item={item}
					response={response}
					originalItemType={originalItemType}
					toggleShowReadMore={toggleShowReadMore}
				/>
			);
		case "writing":
			return (
				<WritingContainer
					item={item}
					response={response}
					originalItemType={originalItemType}
					toggleShowReadMore={toggleShowReadMore}
				/>
			);

		default:
			return null;
	}
};

const StudySessionItemContainer = ({
	item,
	toggleShowReadMore,
	sessionId,
}: Props) => {
	const { metadata } = item;
	const type = metadata?.type!;

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const eventsObject = useMemo(() => {
		return {
			eventName: events.sessionItemViewed,
			itemId: item.id,
			sessionId: sessionId,
			type: type,
		};
	}, [item.id, sessionId, type]);

	useEffect(() => {
		explicitCallTrackCustomEvent(eventsObject);
	}, [eventsObject, explicitCallTrackCustomEvent]);

	return (
		<ResponseRenderer
			item={item}
			response={item.response}
			toggleShowReadMore={toggleShowReadMore}
		/>
	);
};

export default StudySessionItemContainer;
